import React, { useEffect, useMemo, memo, useCallback, useState, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import B2BLayout from '../../../layouts/b2b'
import {
  getMontageId,
  getMontageFormError,
  getMontageFormLoading,
  getVisibleFormData,
  getMontageFormData,
} from './selectors'
import { DefaultJsonFormContextProps } from '../../../components/v2/default-json-form/context/default-json-form-context-props'
import { getMontageEnums, getMontageForm } from '../../../store/montage/action-creators'
import DefaultJsonFormContext from '../../../components/v2/default-json-form/context/default-json-form-context'
import MontageTabs from './ui/tabs/montage-tabs'
import { useAppDispatch } from '../../../hooks/useAppDispatch/useAppDispatch'
import { MontageFormsHeader } from './ui/montage-forms/montage-form-header/montage-form-header'
import { useSaveResult } from './hooks/useSaveResult'
import { Result, Skeleton } from 'antd'

const MontageCard = () => {
  const { id } = useParams<{ id: string }>()

  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const appealId = useSelector(getMontageId)
  const entityType = 'montage'
  const appealsFormIsLoading = useSelector(getMontageFormLoading)
  const appealsFormError = useSelector(getMontageFormError)
  const appealsFormData = useSelector(getMontageFormData)
  const montageFormData = useSelector(getMontageFormData)
  const visibleFormData = useSelector(getVisibleFormData)
  const [enumsRequested, setEnumsRequested] = useState(false)
  const [isStageTransitioning, setIsStageTransitioning] = useState(false)
  const stageTransitionLockRef = useRef(false)

  const { saveResult } = useSaveResult()

  const pageTitle = useMemo(() => `Монтаж №${id}`, [id])

  const breadcrumbs = useMemo(
    () => [
      {
        href: '/montage',
        title: 'Монтаж',
      },
      {
        title: pageTitle,
      },
    ],
    [pageTitle],
  )

  const defaultProps = useMemo(
    (): DefaultJsonFormContextProps => ({
      entityId: id,
      entityType: entityType,
    }),
    [id],
  )

  useEffect(() => {
    let mounted = true
    if (mounted) {
      setEnumsRequested(false)
      dispatch(getMontageForm(id))
    }
    return () => {
      mounted = false
    }
  }, [id, dispatch])

  useEffect(() => {
    if (!id) return
    if (appealsFormData && !appealsFormError && !appealsFormIsLoading && !enumsRequested) {
      setEnumsRequested(true)
      dispatch(getMontageEnums(id))
    }
  }, [appealsFormData, appealsFormError, appealsFormIsLoading, enumsRequested, id, dispatch])

  useEffect(() => {
    if (id === 'new' && appealId) {
      navigate({
        pathname: `/montage/${appealId}`,
      })
    }
  }, [id, appealId])

  const runStageTransition = useCallback(
    async (formData: typeof visibleFormData, is_done?: boolean) => {
      if (stageTransitionLockRef.current) {
        return
      }
      stageTransitionLockRef.current = true
      setIsStageTransitioning(true)
      try {
        await saveResult(
          id,
          formData,
          montageFormData?.stages?.current,
          true,
          is_done,
          montageFormData?.stages,
        )
      } finally {
        stageTransitionLockRef.current = false
        setIsStageTransitioning(false)
      }
    },
    [id, montageFormData, saveResult],
  )

  const handleToOrder = useCallback(async () => {
    await runStageTransition(visibleFormData, undefined)
  }, [runStageTransition, visibleFormData])

  const handleToFinish = useCallback(async () => {
    await runStageTransition(visibleFormData, true)
  }, [runStageTransition, visibleFormData])

  const setCaseStage = useCallback(
    async data => {
      await runStageTransition(data, false)
    },
    [runStageTransition],
  )

  const handleToNotAccepted = useCallback(
    async data => {
      const formData = visibleFormData && visibleFormData?.resultObject
      await setCaseStage({ resultObject: { ...formData, ...data, to_stage: 'not_accepted' } })
    },
    [visibleFormData, setCaseStage],
  )

  if (!id) return null

  return (
    <B2BLayout breadcrumbs={breadcrumbs} pageTitle={pageTitle}>
      {appealsFormIsLoading && <Skeleton active />}
      {!appealsFormIsLoading && appealsFormError && (
        <Result
          status={typeof appealsFormError === 'object' ? appealsFormError.statusCode : 'error'}
          title="Ошибка"
          subTitle={
            typeof appealsFormError === 'object' ? appealsFormError.message : appealsFormError
          }
        />
      )}
      {!appealsFormIsLoading && !appealsFormError && appealsFormData && (
        <>
          <DefaultJsonFormContext.Provider value={defaultProps}>
            <MontageFormsHeader
              id={id}
              isFinished={
                montageFormData?.stages &&
                montageFormData?.stages?.current ===
                  Object.keys(montageFormData?.stages?.items).length - 1
              }
              stage={montageFormData?.stages?.current}
              toOrder={handleToOrder}
              toFinish={handleToFinish}
              toNotAccepted={handleToNotAccepted}
              showTitle={false}
              isStageTransitioning={isStageTransitioning}
            />
            <MontageTabs id={id} />
          </DefaultJsonFormContext.Provider>
        </>
      )}
    </B2BLayout>
  )
}

MontageCard.displayName = 'MontageCard'

export default memo(MontageCard)
